/**
 * Canonical product positioning. SEO, social cards and marketing copy read
 * from here so the promise is stated one way everywhere.
 */
export const PRODUCT = {
  name: "GenieBot",
  promise: "Learn AI. Create with AI. Get things done.",
  tagline: "Learn, Create, Get Things Done",
  description:
    "GenieBot helps you learn AI, create content, and get real work done — with free AI chat, practical tools, and guided lessons from AI Kenny. Operated by Kenneth Gordon.",
  shortDescription: "Learn AI, create with AI, and get things done in one free platform.",
  builtBy: "Kenneth Gordon",
} as const;

export type GoalId = "learn" | "create" | "work" | "secure" | "build";

export interface Goal {
  id: GoalId;
  label: string;
  outcome: string;
  /** Where a signed-out visitor or a new user starts for this goal. */
  start: { label: string; href: string };
  /** A deterministic next step once they've started. */
  next: { label: string; href: string; why: string };
}

export const GOALS: Goal[] = [
  {
    id: "learn",
    label: "Learn AI",
    outcome: "Understand AI, step by step, with short lessons and quick checks.",
    start: { label: "Open the Learning Hub", href: "/learn" },
    next: { label: "Start the Prompting track", href: "/learn/prompting", why: "Prompting is the fastest skill to improve every AI answer." },
  },
  {
    id: "create",
    label: "Create Content",
    outcome: "Write, design images and plan videos with AI help.",
    start: { label: "Browse Create tools", href: "/tools?group=Create" },
    next: { label: "Make an image", href: "/ai-image-generator", why: "You picked Create Content — images are a quick first win." },
  },
  {
    id: "work",
    label: "Improve Work",
    outcome: "Draft, summarize, translate and organize everyday tasks.",
    start: { label: "Start a chat", href: "/chat" },
    next: { label: "Try Productivity tools", href: "/tools?group=Productivity", why: "You picked Improve Work — these tools handle daily tasks." },
  },
  {
    id: "secure",
    label: "Stay Secure",
    outcome: "Protect your data, spot threats and use AI safely.",
    start: { label: "Open the security checklist", href: "/security-checklist" },
    next: { label: "Start the AI Safety track", href: "/learn/ai-safety", why: "You asked for security tips — this track covers safe AI use." },
  },
  {
    id: "build",
    label: "Build Something",
    outcome: "Make chatbots, workflows and automations without code.",
    start: { label: "Browse Build tools", href: "/tools?group=Build" },
    next: { label: "Build a chatbot", href: "/chatbot-builder", why: "You picked Build Something — a chatbot is a good first project." },
  },
];

export const getGoal = (id: GoalId | null | undefined) => GOALS.find((g) => g.id === id);
